import { useCallback, useEffect, useRef } from 'react'

const HUBSPOT_HIDE_SELECTORS = [
	'#hubspot-messages-iframe-container',
	'#hubspot-conversations-iframe',
	'#hubspot-conversations-inline-parent',
]

const BODY_CLASS = 'typeform-survey-open'

export function syncHubspotVisibilityFromBodyClass() {
	if (!document.body.classList.contains(BODY_CLASS)) {
		return
	}
	closeHubspotWidget()
	setHubspotElementsHidden(true)
}

function closeHubspotWidget() {
	try {
		window.HubSpotConversations?.widget?.close?.()
	} catch {
		// HubSpot may not be loaded yet
	}
}

function setHubspotElementsHidden(hidden: boolean) {
	for (const selector of HUBSPOT_HIDE_SELECTORS) {
		document.querySelectorAll(selector).forEach((node) => {
			const el = node as HTMLElement
			if (hidden) {
				el.style.setProperty('display', 'none', 'important')
				el.style.setProperty('pointer-events', 'none', 'important')
				el.style.setProperty('visibility', 'hidden', 'important')
			} else {
				el.style.removeProperty('display')
				el.style.removeProperty('pointer-events')
				el.style.removeProperty('visibility')
			}
		})
	}
}

export function useHubspotChatVisibility() {
	const chatRef = useRef<HTMLElement | null>(null)

	const resolveChatElement = useCallback(() => {
		const el = document.getElementById('hubspot-messages-iframe-container')
		if (el) {
			chatRef.current = el
		}
	}, [])

	const hide = useCallback(() => {
		resolveChatElement()
		closeHubspotWidget()
		setHubspotElementsHidden(true)
	}, [resolveChatElement])

	const show = useCallback(() => {
		resolveChatElement()
		setHubspotElementsHidden(false)
	}, [resolveChatElement])

	useEffect(() => {
		const observer = new MutationObserver(() => {
			resolveChatElement()
		})
		observer.observe(document.body, { childList: true, subtree: true })

		return () => observer.disconnect()
	}, [resolveChatElement])

	return { hide, show }
}

/** Hide HubSpot chat while enabled; re-applies hide if the widget re-attaches to the DOM. */
export function useHideHubspotWhileMounted(enabled = true) {
	const { hide, show } = useHubspotChatVisibility()

	useEffect(() => {
		if (!enabled) {
			return
		}

		document.body.classList.add(BODY_CLASS)
		hide()

		const observer = new MutationObserver(() => {
			hide()
		})
		observer.observe(document.body, { childList: true, subtree: true })

		return () => {
			observer.disconnect()
			document.body.classList.remove(BODY_CLASS)
			show()
		}
	}, [enabled, hide, show])

	return { hide, show }
}
